import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";

const STORAGE_KEY = "admin_theme_v2";

interface ThemeConfig {
  preset: string;
  overlayOpacity: number; // 0–0.40
  warmth: number;         // -1 (cool) to 1 (warm), 0 = neutral white
  accentHue: number;      // 0–360
  /** Optional overrides written directly to document.documentElement CSS vars */
  cssVars?: Record<string, string>;
}

/**
 * CSS variable values from before the contrast uplift.
 * Stored so "Midnight (Original)" truly restores the pre-task palette.
 * Original: card/popover 8%, border/input 20%, muted 15%, secondary 15%.
 */
const ORIGINAL_CSS_VARS: Record<string, string> = {
  "--foreground":         "0 0% 98%",
  "--card":               "0 0% 8%",
  "--card-foreground":    "0 0% 98%",
  "--popover":            "0 0% 8%",
  "--popover-foreground": "0 0% 98%",
  "--secondary":          "0 0% 15%",
  "--muted":              "0 0% 15%",
  "--muted-foreground":   "0 0% 85%",
  "--border":             "0 0% 20%",
  "--input":              "0 0% 20%",
};

const DEFAULTS: ThemeConfig = {
  preset: "midnight-original",
  overlayOpacity: 0,
  warmth: 0,
  accentHue: 265,
  cssVars: ORIGINAL_CSS_VARS,
};

// --- presets ------------------------------------------------------------

interface Preset {
  id: string;
  label: string;
  config: ThemeConfig;
  swatch: [string, string]; // [bg, card] approximate rendered colors
}

const PRESETS: Preset[] = [
  {
    id: "midnight",
    label: "Midnight",
    config: { preset: "midnight", overlayOpacity: 0, warmth: 0, accentHue: 265 },
    swatch: ["#0d0d0d", "#333333"],
  },
  {
    id: "midnight-original",
    label: "Midnight (Original)",
    config: { preset: "midnight-original", overlayOpacity: 0, warmth: 0, accentHue: 265, cssVars: ORIGINAL_CSS_VARS },
    swatch: ["#0a0a0a", "#111111"],
  },
  {
    id: "charcoal",
    label: "Charcoal",
    config: { preset: "charcoal", overlayOpacity: 0.10, warmth: 0, accentHue: 265 },
    swatch: ["#222222", "#2e2e2e"],
  },
  {
    id: "storm",
    label: "Storm",
    config: { preset: "storm", overlayOpacity: 0.22, warmth: 0, accentHue: 240 },
    swatch: ["#383838", "#444444"],
  },
  {
    id: "warm",
    label: "Warm",
    config: { preset: "warm", overlayOpacity: 0.14, warmth: 0.75, accentHue: 28 },
    swatch: ["#211d18", "#2d2720"],
  },
  {
    id: "slate",
    label: "Slate",
    config: { preset: "slate", overlayOpacity: 0.12, warmth: -0.7, accentHue: 210 },
    swatch: ["#16181f", "#20242f"],
  },
];

// --- helpers -------------------------------------------------------------

function loadConfig(): ThemeConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ThemeConfig>;
      const merged = { ...DEFAULTS, ...parsed };
      // Only keep stored cssVars when the preset is "midnight-original".
      // All other presets rely on the uplifted :root values in index.css.
      // Stale cssVars from a previous "midnight-original" session would
      // otherwise override the uplifted defaults and make the uplift invisible.
      if (merged.preset !== "midnight-original") {
        delete merged.cssVars;
        // Persist the cleaned config so the stale vars don't accumulate.
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      }
      return merged;
    }
    // First visit: persist the default so subsequent loads read the same
    // values and the widget preset selection reflects "Midnight (Original)".
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULTS));
  } catch { /* ignore */ }
  return DEFAULTS;
}

function saveConfig(cfg: ThemeConfig) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

/** Returns the rgba() string for the overlay based on opacity + warmth */
function overlayBackground(opacity: number, warmth: number): string {
  if (opacity === 0) return "transparent";
  let r: number, g: number, b: number;
  if (warmth >= 0) {
    // warm: white → orange-ish
    r = 255;
    g = Math.round(255 - warmth * 40);
    b = Math.round(255 - warmth * 95);
  } else {
    // cool: white → blue-ish
    const t = -warmth;
    r = Math.round(255 - t * 75);
    g = Math.round(255 - t * 40);
    b = 255;
  }
  return `rgba(${r}, ${g}, ${b}, ${opacity.toFixed(3)})`;
}

const DEFAULT_ACCENT_HSL = "265 50% 50%";

function applyAccent(hue: number) {
  // Keep saturation + lightness from default, just swap hue
  document.documentElement.style.setProperty("--accent", `${hue} 50% 50%`);
}

function resetAccent() {
  document.documentElement.style.setProperty("--accent", DEFAULT_ACCENT_HSL);
}

/** Apply a set of CSS variable overrides to the root element. */
function applyCssVars(vars: Record<string, string> | undefined) {
  if (!vars) return;
  for (const [k, v] of Object.entries(vars)) {
    document.documentElement.style.setProperty(k, v);
  }
}

/**
 * Remove CSS variable overrides that were written by a preset, restoring
 * the values defined in :root (index.css) which represent the uplifted defaults.
 */
function clearCssVars(vars: Record<string, string> | undefined) {
  if (!vars) return;
  for (const k of Object.keys(vars)) {
    document.documentElement.style.removeProperty(k);
  }
}

// --- admin route guard ---------------------------------------------------

function useIsAdminRoute(): boolean {
  const [location] = useLocation();
  return (
    location.startsWith("/admin/") ||
    location.startsWith("/walkin/") ||
    location.startsWith("/manage/") ||
    location.startsWith("/receptionist/") ||
    location.startsWith("/artist-portal/") ||
    location.startsWith("/support/admin")
  );
}

// --- component -----------------------------------------------------------

export default function AdminColorSchemeWidget() {
  const isAdmin = useIsAdminRoute();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState<ThemeConfig>(() => loadConfig());
  const panelRef = useRef<HTMLDivElement>(null);

  // Apply --accent and base CSS vars only on admin routes; always reset on public pages.
  // This prevents saved custom values from leaking into the public site.
  useEffect(() => {
    if (isAdmin) {
      const saved = loadConfig();
      setCfg(saved);
      if (saved.accentHue !== DEFAULTS.accentHue) applyAccent(saved.accentHue);
      else resetAccent();
      // Re-apply any base CSS variable overrides stored with the config
      if (saved.cssVars) applyCssVars(saved.cssVars);
    } else {
      resetAccent();
      // Clear any active CSS var overrides when leaving admin routes
      clearCssVars(ORIGINAL_CSS_VARS);
    }
  }, [isAdmin]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const update = useCallback((partial: Partial<ThemeConfig>) => {
    setCfg(prev => {
      const next = { ...prev, ...partial };
      saveConfig(next);
      if (partial.accentHue !== undefined) applyAccent(partial.accentHue);
      return next;
    });
  }, []);

  const applyPreset = useCallback((preset: Preset) => {
    // Clear any previously applied CSS var overrides before applying the new preset
    clearCssVars(ORIGINAL_CSS_VARS);
    setCfg(preset.config);
    saveConfig(preset.config);
    applyAccent(preset.config.accentHue);
    applyCssVars(preset.config.cssVars);
  }, []);

  const reset = useCallback(() => {
    // Remove any CSS var overrides so :root values (uplifted defaults) take effect
    clearCssVars(ORIGINAL_CSS_VARS);
    setCfg(DEFAULTS);
    saveConfig(DEFAULTS);
    resetAccent();
  }, []);

  if (!isAdmin) return null;

  const bg = overlayBackground(cfg.overlayOpacity, cfg.warmth);
  const isDefault = cfg.overlayOpacity === 0 && cfg.accentHue === DEFAULTS.accentHue;

  return (
    <>
      {/* Lightening overlay — below the widget, above page content */}
      {cfg.overlayOpacity > 0 && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9990,
            pointerEvents: "none",
            background: bg,
          }}
        />
      )}

      {/* Widget anchor */}
      <div ref={panelRef} style={{ position: "fixed", bottom: "calc(8px + var(--bottom-banner-h, 0px))", right: 16, zIndex: 9999, transition: "bottom 0.2s ease" }}>

        {/* Panel */}
        {open && (
          <div
            data-testid="admin-color-scheme-panel"
            style={{
              position: "absolute",
              bottom: "calc(100% + 10px)",
              right: 0,
              width: 272,
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: 12,
              boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
              padding: "16px",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Display Theme
              </span>
              {!isDefault && (
                <button
                  onClick={reset}
                  data-testid="admin-theme-reset"
                  style={{ fontSize: 11, color: "#666", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  Reset
                </button>
              )}
            </div>

            {/* Preset swatches */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 18 }}>
              {PRESETS.map(preset => {
                const active = cfg.preset === preset.id;
                return (
                  <button
                    key={preset.id}
                    data-testid={`admin-theme-preset-${preset.id}`}
                    onClick={() => applyPreset(preset)}
                    title={preset.label}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    {/* Swatch preview */}
                    <div style={{
                      width: 40,
                      height: 28,
                      borderRadius: 6,
                      overflow: "hidden",
                      border: `2px solid ${active ? "#a78bfa" : "#333"}`,
                      outline: active ? "1px solid #7c3aed" : "none",
                      outlineOffset: 1,
                      flexShrink: 0,
                    }}>
                      <div style={{ height: "50%", background: preset.swatch[0] }} />
                      <div style={{ height: "50%", background: preset.swatch[1] }} />
                    </div>
                    <span style={{ fontSize: 10, color: active ? "#a78bfa" : "#666", whiteSpace: "nowrap" }}>
                      {preset.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "#2a2a2a", marginBottom: 14 }} />

            {/* Sliders */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Slider
                label="Brightness"
                value={cfg.overlayOpacity}
                min={0}
                max={0.40}
                step={0.01}
                displayValue={`${Math.round(cfg.overlayOpacity * 100 / 0.40)}%`}
                onChange={v => update({ overlayOpacity: v, preset: "custom" })}
                accentColor="#a78bfa"
              />
              <Slider
                label="Warmth"
                value={(cfg.warmth + 1) / 2}     // remap -1..1 → 0..1 for slider
                min={0}
                max={1}
                step={0.01}
                displayValue={cfg.warmth > 0.05 ? "Warm" : cfg.warmth < -0.05 ? "Cool" : "Neutral"}
                onChange={v => update({ warmth: v * 2 - 1, preset: "custom" })}
                accentColor="#fb923c"
              />
              <Slider
                label="Accent Hue"
                value={cfg.accentHue}
                min={0}
                max={360}
                step={1}
                displayValue={hueLabel(cfg.accentHue)}
                onChange={v => update({ accentHue: v, preset: "custom" })}
                accentColor={`hsl(${cfg.accentHue}, 60%, 65%)`}
              />
            </div>
          </div>
        )}

        {/* Toggle button */}
        <button
          data-testid="admin-color-scheme-toggle"
          onClick={() => setOpen(o => !o)}
          title="Display theme"
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: open ? "#2d2040" : "#1a1a1a",
            border: `1.5px solid ${open ? "#7c3aed" : "#7c3aed88"}`,
            boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "border-color 0.15s, background 0.15s",
          }}
        >
          <PaletteIcon active={open} hasCustom={!isDefault} />
        </button>
      </div>
    </>
  );
}

// --- sub-components ------------------------------------------------------

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  onChange: (v: number) => void;
  accentColor: string;
}

function Slider({ label, value, min, max, step, displayValue, onChange, accentColor }: SliderProps) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: "#888", fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 11, color: "#bbb" }}>{displayValue}</span>
      </div>
      <div style={{ position: "relative", height: 4, borderRadius: 2, background: "#333" }}>
        <div style={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "100%",
          width: `${pct}%`,
          borderRadius: 2,
          background: accentColor,
        }} />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
            margin: 0,
            padding: 0,
          }}
        />
        {/* Thumb */}
        <div style={{
          position: "absolute",
          top: "50%",
          left: `${pct}%`,
          transform: "translate(-50%, -50%)",
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: accentColor,
          border: "2px solid #1a1a1a",
          pointerEvents: "none",
        }} />
      </div>
    </div>
  );
}

function PaletteIcon({ active, hasCustom }: { active: boolean; hasCustom: boolean }) {
  const color = active ? "#a78bfa" : hasCustom ? "#7c6aaa" : "#666";
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r=".5" fill={color} />
      <circle cx="17.5" cy="10.5" r=".5" fill={color} />
      <circle cx="8.5" cy="7.5" r=".5" fill={color} />
      <circle cx="6.5" cy="12.5" r=".5" fill={color} />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
  );
}

function hueLabel(hue: number): string {
  if (hue < 15 || hue >= 345) return "Red";
  if (hue < 45) return "Orange";
  if (hue < 75) return "Yellow";
  if (hue < 150) return "Green";
  if (hue < 200) return "Teal";
  if (hue < 255) return "Blue";
  if (hue < 285) return "Purple";
  if (hue < 315) return "Magenta";
  return "Pink";
}
